import React, { useState, useEffect } from 'react';
import {
  MapIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  CloudIcon
} from '@heroicons/react/24/outline';
import { Mission } from '../../types';
import { parseCoordinateInput, formatCoordinates } from '../../utils/coordinateUtils';
import LocationMap from '../LocationMap';
import weatherService from '../../services/weatherService';

interface LZSafetyTabProps {
  mission: Mission;
  onUpdate: (updates: Partial<Mission>) => void;
  canEdit: boolean;
}

const LZSafetyTab: React.FC<LZSafetyTabProps> = ({ mission, onUpdate, canEdit }) => {
  const [localValues, setLocalValues] = useState<Partial<Mission>>({});
  const [lzCoordinateInput, setLzCoordinateInput] = useState('');
  const [lzCoordinateError, setLzCoordinateError] = useState<string | null>(null);
  const [weather, setWeather] = useState<any>(null);
  const [weatherLoading, setWeatherLoading] = useState(false);

  const handleFieldChange = (field: keyof Mission, value: any) => {
    setLocalValues({ ...localValues, [field]: value });
  };

  const handleImmediateSave = (field: keyof Mission, value: any) => {
    // For select fields, save immediately with the new value
    if (value !== mission[field]) {
      onUpdate({ [field]: value });
      setLocalValues({ ...localValues, [field]: value });
    }
  };

  const handleFieldBlur = (field: keyof Mission) => {
    if (localValues[field] !== undefined && localValues[field] !== mission[field]) {
      onUpdate({ [field]: localValues[field] });
      setLocalValues({ ...localValues, [field]: undefined });
    }
  };

  // Handle LZ coordinate input — accepts any supported format (decimal, DMS,
  // degrees-decimal-minutes, NMEA; comma- or space-separated)
  const handleLzCoordinateBlur = () => {
    const input = lzCoordinateInput.trim();
    if (!input) {
      setLzCoordinateError(null);
      return;
    }

    const pair = parseCoordinateInput(input);
    if (pair) {
      setLzCoordinateError(null);
      onUpdate({
        lzGpsLat: pair.latitude.decimal,
        lzGpsLong: pair.longitude.decimal
      });
      setLzCoordinateInput(`${pair.latitude.decimal}, ${pair.longitude.decimal}`);
    } else {
      setLzCoordinateError('Could not parse coordinates. Try: 48.813826, -123.648911 or 48°48\'50"N 123°38\'56"W');
    }
  };

  useEffect(() => {
    const lat = mission.lzGpsLat || mission.sceneGpsLat;
    const lon = mission.lzGpsLong || mission.sceneGpsLong;
    if (lat && lon) {
      setWeatherLoading(true);
      weatherService.getMetarFromNOAA(lat, lon)
        .then(data => { if (data) setWeather(data); })
        .catch(err => console.error('Weather fetch error:', err))
        .finally(() => setWeatherLoading(false));
    }
  }, [mission.lzGpsLat, mission.lzGpsLong, mission.sceneGpsLat, mission.sceneGpsLong]);

  // Initialize LZ coordinates when mission loads
  React.useEffect(() => {
    if (mission.lzGpsLat && mission.lzGpsLong) {
      setLzCoordinateInput(`${mission.lzGpsLat}, ${mission.lzGpsLong}`);
    }
  }, [mission.lzGpsLat, mission.lzGpsLong]);

  const renderEditableField = (
    field: keyof Mission,
    label: string,
    type: 'text' | 'textarea' | 'select' = 'text',
    options?: { value: string; label: string }[]
  ) => {
    const rawValue = localValues[field] !== undefined ? localValues[field] : mission[field];
    const currentValue = (rawValue ?? '') as string;

    return (
      <div className="py-3">
        <label className="text-sm font-medium text-gray-700 mb-1 block">{label}</label>
        
        {canEdit ? (
          <div>
            {type === 'textarea' ? (
              <textarea
                value={currentValue || ''}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                onBlur={() => handleFieldBlur(field)}
                className="form-input w-full"
                rows={3}
                placeholder={`Enter ${label.toLowerCase()}...`}
              />
            ) : type === 'select' ? (
              <select
                value={currentValue || ''}
                onChange={(e) => {
                  handleFieldChange(field, e.target.value);
                  handleImmediateSave(field, e.target.value); // Use immediate save for selects
                }}
                className="form-input w-full"
              >
                <option value="">Select...</option>
                {options?.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={currentValue || ''}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                onBlur={() => handleFieldBlur(field)}
                className="form-input w-full"
                placeholder={`Enter ${label.toLowerCase()}...`}
              />
            )}
          </div>
        ) : (
          <p className="text-gray-400">{String(mission[field] || '') || '--'}</p>
        )}
      </div>
    );
  };

  const surfaceTypes = [
    { value: 'PAVED', label: 'Paved' },
    { value: 'GRAVEL', label: 'Gravel' },
    { value: 'GRASS', label: 'Grass' },
    { value: 'DIRT', label: 'Dirt' },
    { value: 'SAND', label: 'Sand' },
    { value: 'SNOW', label: 'Snow' },
    { value: 'WATER', label: 'Water' },
    { value: 'OTHER', label: 'Other' }
  ];

  return (
    <div className="space-y-6">
      {/* Landing Zone Information */}
      <div className="card">
        <div className="card-header card-header-brand">
          <h3 className="card-title flex items-center">
            <MapIcon className="h-5 w-5 mr-2" />
            Landing Zone Information
          </h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="py-3">
              <label className="text-sm font-medium text-gray-700 mb-1 block">LZ Coordinates (Lat, Long)</label>
              {canEdit ? (
                <>
                  <input
                    type="text"
                    value={lzCoordinateInput}
                    onChange={(e) => {
                      setLzCoordinateInput(e.target.value);
                      setLzCoordinateError(null);
                    }}
                    onBlur={handleLzCoordinateBlur}
                    onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                    className={`form-input w-full ${lzCoordinateError ? 'border-red-300 focus:border-red-500' : ''}`}
                    placeholder="48.813826, -123.648911 or 48°48'50&quot;N 123°38'56&quot;W"
                  />
                  {lzCoordinateError && (
                    <p className="mt-1 text-sm text-red-600">{lzCoordinateError}</p>
                  )}
                </>
              ) : (
                <p className="text-gray-900">
                  {mission.lzGpsLat && mission.lzGpsLong
                    ? `${mission.lzGpsLat}, ${mission.lzGpsLong}`
                    : 'Not specified'}
                </p>
              )}
              {mission.lzGpsLat && mission.lzGpsLong && (
                <div className="mt-2 text-sm text-gray-600">
                  Aviation Format: {formatCoordinates(mission.lzGpsLat, mission.lzGpsLong, 'aviation')}
                </div>
              )}
            </div>
          </div>
          
          <div>
            {renderEditableField(
              'lzSurfaceType',
              'Surface Type',
              'select',
              surfaceTypes
            )}
            
            <div className="py-3">
              <label className="text-sm font-medium text-gray-700">Landing Zone Size</label>
              <div className="mt-2 space-y-2">
                <label className="flex items-center">
                  <input type="radio" name="lzSize" value="100x100" className="mr-2" disabled={!canEdit} />
                  <span className="text-sm">100' x 100' (Minimum)</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="lzSize" value="150x150" className="mr-2" disabled={!canEdit} />
                  <span className="text-sm">150' x 150' (Preferred)</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="lzSize" value="200x200" className="mr-2" disabled={!canEdit} />
                  <span className="text-sm">200' x 200' (Ideal)</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* LZ Map */}
      {(mission.lzGpsLat && mission.lzGpsLong) || (mission.sceneGpsLat && mission.sceneGpsLong) ? (
        <div className="card">
          <div className="card-header card-header-brand">
            <h3 className="card-title flex items-center">
              <MapIcon className="h-5 w-5 mr-2" />
              Landing Zone Map
            </h3>
          </div>
          <LocationMap
            latitude={mission.lzGpsLat || mission.sceneGpsLat!}
            longitude={mission.lzGpsLong || mission.sceneGpsLong!}
            title={mission.lzGpsLat ? 'Landing Zone' : 'Scene Location'}
            height="350px"
          />
        </div>
      ) : null}

      {/* Hazards & Obstacles */}
      <div className="card">
        <div className="card-header card-header-brand">
          <h3 className="card-title flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            Hazards & Obstacles
          </h3>
        </div>
        
        {renderEditableField(
          'lzObstaclesWires',
          'Obstacles/Wires/Hazards',
          'textarea'
        )}
        
        <div className="mt-4">
          <label className="text-sm font-medium text-gray-700 mb-2 block">Common Hazards Checklist</label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input type="checkbox" className="mr-2" disabled={!canEdit} />
              <span className="text-sm">Power lines within 500 feet</span>
            </label>
            <label className="flex items-center">
              <input type="checkbox" className="mr-2" disabled={!canEdit} />
              <span className="text-sm">Trees or tall structures nearby</span>
            </label>
            <label className="flex items-center">
              <input type="checkbox" className="mr-2" disabled={!canEdit} />
              <span className="text-sm">Sloped terrain (&gt;10 degrees)</span>
            </label>
            <label className="flex items-center">
              <input type="checkbox" className="mr-2" disabled={!canEdit} />
              <span className="text-sm">Loose debris or objects</span>
            </label>
            <label className="flex items-center">
              <input type="checkbox" className="mr-2" disabled={!canEdit} />
              <span className="text-sm">Vehicle traffic nearby</span>
            </label>
            <label className="flex items-center">
              <input type="checkbox" className="mr-2" disabled={!canEdit} />
              <span className="text-sm">Crowd control required</span>
            </label>
          </div>
        </div>
      </div>

      {/* Weather Conditions */}
      <div className="card">
        <div className="card-header card-header-brand">
          <h3 className="card-title flex items-center">
            <CloudIcon className="h-5 w-5 mr-2" />
            Weather Conditions
          </h3>
        </div>
        
        {renderEditableField(
          'weatherConditions',
          'Current Conditions',
          'textarea'
        )}
        
        {renderEditableField(
          'weatherImpact',
          'Impact on Operations',
          'textarea'
        )}
        
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Weather Minimums</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• VFR Day: 1000' ceiling, 3 miles visibility</li>
            <li>• VFR Night: 2000' ceiling, 5 miles visibility</li>
            <li>• IFR: As per aircraft/pilot certification</li>
            <li>• Max wind: 35 knots sustained, 45 knots gusts</li>
          </ul>
        </div>

        {weatherLoading && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg text-center text-sm text-blue-700">
            Loading weather data...
          </div>
        )}
        {weather && !weatherLoading && (
          <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-yellow-900">
                Nearest Airport METAR - {weather.station} ({weather.distance}km away)
              </h4>
              <button
                onClick={() => {
                  const lat = mission.lzGpsLat || mission.sceneGpsLat;
                  const lon = mission.lzGpsLong || mission.sceneGpsLong;
                  if (lat && lon) {
                    setWeatherLoading(true);
                    weatherService.getMetarFromNOAA(lat, lon)
                      .then(data => { if (data) setWeather(data); })
                      .catch(err => console.error('Weather refresh error:', err))
                      .finally(() => setWeatherLoading(false));
                  }
                }}
                className="text-xs text-yellow-700 hover:text-yellow-900 underline"
              >
                Refresh Weather
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-yellow-700">Flight Rules:</span>
                <span className={`ml-2 font-bold ${
                  weather.flightRules === 'VFR' ? 'text-green-600' :
                  weather.flightRules === 'MVFR' ? 'text-blue-600' :
                  weather.flightRules === 'IFR' ? 'text-red-600' : 'text-purple-600'
                }`}>{weather.flightRules}</span>
              </div>
              <div>
                <span className="text-yellow-700">Visibility:</span>
                <span className="ml-2 font-medium text-yellow-900">{weather.visibility?.toFixed(1)} km</span>
              </div>
              <div>
                <span className="text-yellow-700">Wind:</span>
                <span className="ml-2 font-medium text-yellow-900">
                  {weather.windDirection}° @ {Math.round(weather.windSpeed)} km/h
                  {weather.windGust ? ` G${Math.round(weather.windGust)}` : ''}
                </span>
              </div>
              <div>
                <span className="text-yellow-700">Temp/Dewpoint:</span>
                <span className="ml-2 font-medium text-yellow-900">{weather.temperature}°C / {weather.dewpoint}°C</span>
              </div>
            </div>
            {weather.rawText && (
              <div className="mt-2 p-2 bg-yellow-100 rounded font-mono text-xs">{weather.rawText}</div>
            )}
          </div>
        )}
      </div>

      {/* LZ Notes */}
      <div className="card">
        <div className="card-header card-header-brand">
          <h3 className="card-title flex items-center">
            <DocumentTextIcon className="h-5 w-5 mr-2" />
            Landing Zone Notes
          </h3>
        </div>
        
        {renderEditableField(
          'lzNotes',
          'Additional LZ Information',
          'textarea'
        )}
        
        <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
          <h4 className="text-sm font-medium text-yellow-900 mb-2">LZ Coordinator Responsibilities</h4>
          <ul className="text-sm text-yellow-800 space-y-1">
            <li>• Ensure LZ is clear of people and obstacles</li>
            <li>• Mark LZ with flares, lights, or cones if available</li>
            <li>• Brief ground personnel on approach/departure paths</li>
            <li>• Maintain radio contact with aircraft</li>
            <li>• Have fire suppression equipment ready</li>
          </ul>
        </div>
      </div>

      {canEdit && (
        <div className="text-sm text-gray-500 italic">
          Tip: All fields auto-save when you click away or press Tab
        </div>
      )}
    </div>
  );
};

export default LZSafetyTab;